import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth } from "../AuthContext";
import useAuthenticatedFetch from "../hooks/useAuthenticatedFetch";
import { useTranslation } from "react-i18next";
import LocalizedTextField from "../components/common/LocalizedTextField";

const RavEditor = ({ onIsDirtyChange }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [rabanim, setRabanim] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const fetchWithAuth = useAuthenticatedFetch();
  const [hasFetched, setHasFetched] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ravToDelete, setRavToDelete] = useState(null);

  useEffect(() => {
    if (onIsDirtyChange) {
      onIsDirtyChange(isDirty); // Notifier le parent chaque fois que isDirty change
    }
  }, [isDirty, onIsDirtyChange]);

  useEffect(() => {
    const fetchData = async () => {
      if (hasFetched) return; // Empêcher un double fetch

      try {
        const response = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/rav/getRabanim?communityId=${user.communityId}`
        );
        const data = await response.json();
        setRabanim(data);
        setErrors(new Array(data.length).fill(false));
        setHasFetched(true);
      } catch (error) {
        console.error(error.message);
        setErrorMessage(t("rav.errors.fetchError"));
        setHasFetched(true);
      }
    };

    fetchData();
  }, [user.communityId, fetchWithAuth, t, hasFetched]);

  const handleAddRow = () => {
    setRabanim([...rabanim, { ravId: null, ravName: "" }]);
    setErrors([...errors, false]);
    setIsDirty(true);
  };

  const handleUpdateRow = (index, field, value) => {
    const updatedRabanim = [...rabanim];
    updatedRabanim[index][field] = value;
    setRabanim(updatedRabanim);
    // Réinitialiser l'erreur si le champ est rempli
    if (field === "ravName" && value.trim() !== "") {
      const updatedErrors = [...errors];
      updatedErrors[index] = false;
      setErrors(updatedErrors);
    }
    setIsDirty(true);
  };

  const handleDeleteClick = (index) => {
    setRavToDelete(rabanim[index]);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRavToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!ravToDelete) return;

    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/rav/deleteRav`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ravId: ravToDelete.ravId,
            communityId: user.communityId,
          }),
        }
      );

      if (response.ok) {
        const updatedRabanim = rabanim.filter((rav) => rav.ravId !== ravToDelete.ravId);
        setRabanim(updatedRabanim);
        setSuccessMessage(t("rav.success.delete"));
        setIsDirty(false);
        if (onIsDirtyChange) {
          onIsDirtyChange(false);
        }
      } else {
        setErrorMessage(t("rav.errors.deleteError"));
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(t("rav.errors.deleteError"));
    }

    setDeleteDialogOpen(false);
    setRavToDelete(null);
  };

  const validateFields = () => {
    const updatedErrors = rabanim.map((rav) => !rav.ravName.trim());
    setErrors(updatedErrors);
    return !updatedErrors.includes(true); // Retourne false s'il y a des erreurs
  };

  const handleSave = async () => {
    if (!validateFields()) {
      setErrorMessage(t("common.validationError")); // Message d'erreur global
      return;
    }

    try {
      // Appeler l'API pour sauvegarder les Rabanim
      await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/rav/saveRabanim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ communityId: user.communityId, rabanim }),
      });
      setSuccessMessage(t("rav.success.save"));
      setIsDirty(false);
    } catch (error) {
      console.error(error.message);
      setErrorMessage(t("rav.errors.saveError"));
    }
  };

  return (
    <Box sx={{ mt: 2, width: "100%" }}>
      <Typography variant="h4" gutterBottom>
        {t("rav.title")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {rabanim.map((rav, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              borderBottom: "1px solid #ccc",
              paddingBottom: 1,
            }}
          >
            <LocalizedTextField
              label={t("rav.name")}
              value={rav.ravName}
              onChange={(e) =>
                handleUpdateRow(index, "ravName", e.target.value)
              }
              fullWidth
              error={errors[index]}
              helperText={errors[index] ? t("common.required") : ""}
            />

            {/* Bouton supprimer */}
            <IconButton
              onClick={() => handleDeleteClick(index)}
              aria-label={t("rav.delete")}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}

        {/* Bouton pour ajouter un Rav */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            onClick={handleAddRow}
            aria-label={t("rav.add")}
            color="primary"
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Bouton sauvegarder */}
      <Box sx={{ mt: 2 }}>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
        >
          {t("rav.save")}
        </Button>
      </Box>

      {/* Boîte de dialogue de confirmation */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          {t("rav.deleteConfirmation.title")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t("rav.deleteConfirmation.message", {
              ravName: ravToDelete?.ravName,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        message={successMessage}
      />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage("")}
        message={errorMessage}
      />
    </Box>
  );
};

export default RavEditor;